/**
 * DEAD SIGNAL — Active chapter registry (ADR-0001)
 *
 * Holds the chapter content the engine is currently playing. `loadChapter()`
 * (content/chapters/index.js) populates this during boot, before the playable
 * scenes render. The synchronous accessors below are what the engine calls;
 * they read from the active chapter so the content can live in a lazily
 * imported chunk rather than the entry bundle.
 *
 * Accessors return null/empty before a chapter is loaded. The boot gate
 * guarantees a chapter is loaded before any scene renders (BootScreen awaits
 * loadChapter at dismissSplash; App renders the HUD only when chapterLoaded).
 */
let active = null;

export function setActiveChapter(chapter) {
  active = chapter;
}

export function getActiveChapter() {
  return active;
}

/** Scene definition by id, or null when no chapter is loaded or the id is unknown. */
export function getScene(sceneId) {
  return active?.scenes?.[sceneId] ?? null;
}

/** Scenes whose `chapter` field matches (back-compat with the old manifest API). */
export function getScenesForChapter(chapter) {
  if (!active?.scenes) return [];
  return Object.values(active.scenes).filter((s) => s.chapter === chapter);
}

/** The active chapter's scene graph (navigation topology), or {} before load. */
export function getSceneGraph() {
  return active?.sceneGraph ?? {};
}

/** The active chapter's hint chains (objective -> escalation), or {} before load. */
export function getHintChains() {
  return active?.hintChains ?? {};
}

/** The active chapter's evidence seed (ids the player starts with), or [] before load. */
export function getEvidenceSeed() {
  return active?.evidenceSeed ?? [];
}

/** The active chapter's case-knowledge chunks (id -> chunk), or {} before load. */
export function getCaseKnowledge() {
  return active?.caseKnowledge ?? {};
}

/** An interview definition by NPC id (e.g. 'hargrove'), or null. */
export function getInterviewDef(id) {
  return active?.interviews?.[id] ?? null;
}

/** An NPC persona def by id (e.g. 'crainePhaseA'), or null. */
export function getPersona(id) {
  return active?.personas?.[id] ?? null;
}

/** The active chapter's debrief observations, or {} before load. */
export function getDebrief() {
  return active?.debriefObservations ?? {};
}
